<template>
    <ElPopover
        v-if="userInfo.customer"
        :hide-after="100"
        :popper-style="{
            inset: '56px 16px auto auto',
            padding: 0
        }"
        :show-arrow="false"
        :width="156"
        placement="bottom-end"
        popper-class="user-popper"
        trigger="hover"
        @after-leave="onUserAfterLeave"
        @before-enter="showUserPopper"
    >
        <template #reference>
            <div class="header-right">
                <div class="user flex items-center">
                    <div class="user-name flex items-center">
                        <!--已完成组织机构认证-->
                        <template v-if="userStore.userInfo.proxyAccount?.applyState">
                            <div class="auth-enterprise">
                                {{ getInstitutionName() }}
                            </div>
                            <div class="auth-name">{{ userStore.userInfo.customer.realName }}</div>
                        </template>
                        <!--未完成组织机构认证-->
                        <template v-else>
                            <div class="auth-text">{{ userStore.userInfo.customer.realName }}</div>
                            <div class="auth-phone">{{ userStore.mobile }}</div>
                        </template>
                    </div>
                    <div v-if="accountStore.accountTypeDesc" class="account-type">
                        {{ accountStore.accountTypeDesc }}
                    </div>
                </div>
            </div>
        </template>
        <template #default>
            <div
                v-loading="loading"
                :element-loading-spinner="defaultLoadingSvg"
                element-loading-svg-view-box="0 0 64 64"
                class="popover-user-info"
            >
                <div class="user-menu" @click="toMePage('info')">账号中心</div>
                <ElPopover
                    v-if="loading || enterpriseList?.length"
                    :hide-after="0"
                    :teleported="false"
                    :visible="enterpriseVisible"
                    :width="300"
                    placement="left"
                    trigger="click"
                    @before-enter="onMouseEnter"
                >
                    <template #reference>
                        <div class="user-menu" @click="onClickChangeEnterprise">切换受邀企业</div>
                    </template>
                    <div
                        v-for="(item, index) in enterpriseList"
                        :key="item.proxyAccountId"
                        :class="enterpriseIndex === index ? 'active' : ''"
                        class="enterprise-wrap"
                        @click="onClickEnterprise(index, item)"
                    >
                        <span class="name">
                            {{ item.enterpriseName }}
                        </span>
                        <span v-if="enterpriseIndex === index" class="iconfont icon-choose"></span>
                    </div>
                </ElPopover>
                <ElPopover
                    v-if="loading || (Boolean(userStore?.userInfo?.customer?.applyState) && mainBodyList?.length)"
                    :hide-after="0"
                    :teleported="false"
                    :visible="mainBodyVisible"
                    :width="300"
                    placement="left"
                    trigger="click"
                >
                    <template #reference>
                        <div class="user-menu" @click="onClickChangeMainBody">切换关联主体</div>
                    </template>
                    <div
                        v-for="item in mainBodyList"
                        :key="item.mainBodyId"
                        :class="currentMainBody === item.mainBodyId ? 'active' : ''"
                        class="enterprise-wrap"
                        @click="onClickMainBody(item)"
                    >
                        <span class="name">
                            {{ item.name }}
                        </span>
                        <span v-if="currentMainBody === item.mainBodyId" class="iconfont icon-choose"></span>
                    </div>
                </ElPopover>
                <div v-if="showMyEnterprise()" class="user-menu" @click="switchMyEnterprise()">返回主账号</div>
                <div class="line"></div>
                <div class="logout">
                    <span @click="logout">退出登录</span>
                </div>
            </div>
        </template>
    </ElPopover>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { ElPopover } from "element-plus";
import { cloneDeep as _cloneDeep } from "lodash";
import { ROLE_TYPE } from "@supplier/meta";
import { useLoading } from "@supplier/hooks";

import type { Ref } from "vue";
import type { CustomerEnterpriseVo, CustomerVo, MainBody } from "@supplier/entity";

import * as loginService from "@/api/loginService";
import { userMessageService } from "@/api/userMessage";
import { getSmsManagementEnterpriseUserAndSubAccountList } from "@/api/subManagementService";
import { useUserStore } from "@/stores/user";
import { useAccountStore } from "@/stores/account";
import { getEnterpriseMainBodyByCustomerId, getInvitedEnterpriseMainBodyList } from "@/api/mainBodyService";
import { useTabsStore } from "@/stores/tabs";
import { getUserInfo } from "@/utils/common";
import { defaultLoadingSvg } from "@/views/layouts/main/loading";
import { useTaskCenterStore } from "@/stores/taskCenter";

interface EnterpriseList {
    proxyAccountId: number;
    mainBodyId: number;
    enterpriseName: string;
    roles: number[];
}

const router = useRouter();
const userStore = useUserStore();
const accountStore = useAccountStore();
const tabStore = useTabsStore();
const taskCenterStore = useTaskCenterStore();
const userInfo: Ref<CustomerVo> = computed(() => userStore.userInfo as unknown as CustomerVo);
const logout = async () => {
    taskCenterStore.resetTaskCenterOnLogout();
    await loginService.logout();
    // ElMessage.success("已退出登录");
    if (userStore.isSAAS) {
        window.location.href = import.meta.env.VITE_LOGIN_APP_URL as string;
    } else {
        await router.push({ path: "/login" });
    }
    userStore.clear();
};

const toMePage = (path: string) => {
    router.push({ path: `/me/${path}` });
};

/** 切换企业，切换身份 */
const enterpriseList = ref<EnterpriseList[]>([]);
const enterpriseIndex: Ref<number | null> = ref(-1);
// 设置企业列表
const setEnterpriseList = async () => {
    if (!accountStore?.mainBody?.id) {
        const mainBody = await getEnterpriseMainBodyByCustomerId(userStore.userInfo.customer.id);
        accountStore.setMainBody(mainBody);
    }
    const result: EnterpriseList[] = [];
    // 查询受邀记录为已确认的数据
    const data: CustomerEnterpriseVo[] = await getSmsManagementEnterpriseUserAndSubAccountList();
    data.forEach((item: CustomerEnterpriseVo) => {
        result.push({
            proxyAccountId: item.account?.id,
            mainBodyId: item.customerEnterprise?.mainBodyId,
            enterpriseName: item.account?.institutionName,
            roles: item.customerEnterprise?.roles || []
        });
    });
    enterpriseList.value = _cloneDeep(result);
    setEnterpriseIndex();
};

// 企业高亮
const setEnterpriseIndex = () => {
    const accountId = accountStore?.proxyActualAccount?.id ? accountStore?.proxyActualAccount?.id : userStore.proxyAccountId;
    const index = enterpriseList.value.findIndex(
        item => item.proxyAccountId === accountId && item.mainBodyId === userStore.userInfo?.mainBody?.id
    );
    enterpriseIndex.value = index >= 0 ? index : null;
    currentMainBody.value = userStore.userInfo?.mainBody?.id;
};

// 点击企业切换企业
const onClickEnterprise = (index: number, item: EnterpriseList) => {
    if (enterpriseIndex.value === index) return;
    enterpriseIndex.value = index;
    loginSwitchProxy(item);
};

// 点击切换主体
const onClickMainBody = (item: { mainBodyId: number; name: string }) => {
    currentMainBody.value = item.mainBodyId;
    loginSwitchMainBody(item.mainBodyId);
};

// 切换主体
const mainBodyList = ref<{ mainBodyId: number; name: string }[]>([]);
// 当前选中高亮的主体ID
const currentMainBody = ref();
// 主体选项列表
const setMainBodyList = async () => {
    mainBodyList.value = [];
    const data: MainBody[] = await getInvitedEnterpriseMainBodyList();
    data.forEach((item: MainBody) => {
        mainBodyList.value.push({
            mainBodyId: item.id,
            name: item.enterpriseName
        });
    });
};
// 切换代理账号
const loginSwitchProxy = async (item: EnterpriseList) => {
    await loginService.loginSwitchProxy(item.proxyAccountId, item.mainBodyId);
    userStore.setProxyAccountId(item.proxyAccountId);
    tabStore.setNextReload(false);
    // 重新查询未读数据;
    await getUnReadMsgNum();
    onUserAfterLeave();
    await afterLoginSwitchProxyJump();
};
// 身份切换跳转完成后，仅保留当前身份落点页签，避免旧身份页签继续触发接口。
const closeOtherTabsAfterJump = async () => {
    await nextTick();
    await tabStore.closeMultipleTab(router.currentRoute.value.fullPath);
};

// 切换企业成功之后页面跳转逻辑
const afterLoginSwitchProxyJump = async () => {
    userStore.setNeedRefreshUserInfo(true);
    // 仅开启待办工作标签时保留原逻辑，不主动清理当前唯一页签。
    const onlyTaskTabOpened = tabStore.tabsMenuList?.length === 1 && tabStore.tabsMenuList[0].path === "/myTask/index";
    setTimeout(() => {
        // WS推送异常断联，兜底
        if (userStore.needRefreshUserInfo) {
            getUserInfo();
            userStore.setNeedRefreshUserInfo(false);
            // 强制刷新页面
            window.location.reload();
        }
    }, 3000);
    setTimeout(async () => {
        // 如果既不是采购客户也不是仓储客户就跳转个人信息页面
        if (isPurchaseCustomer() || isWarehouseCustomer()) {
            await router.push("/");
        } else {
            await router.push({ name: "Info" });
        }
        if (!onlyTaskTabOpened) {
            await closeOtherTabsAfterJump();
        }
    }, 300);
};

// 切换主体
const loginSwitchMainBody = async (mainBodyId: number) => {
    if (!userStore.proxyAccountId && enterpriseIndex.value) {
        const proxyAccountId = enterpriseList.value[enterpriseIndex.value]?.proxyAccountId;
        userStore.setProxyAccountId(proxyAccountId);
    }
    await loginService.loginSwitchMainBody(mainBodyId);
    tabStore.setNextReload(false);
    // 重新查询未读数据;
    await getUnReadMsgNum();
    onUserAfterLeave();
    await afterLoginSwitchProxyJump();
};

// 切换回主账号
const switchMyEnterprise = async () => {
    let mainBodyId = null;
    const mainBody = await getEnterpriseMainBodyByCustomerId(userStore.userInfo.customer.id);
    if (mainBody?.id) {
        mainBodyId = mainBody.id;
    }
    await loginService.loginSwitchMainBody(Number(mainBodyId));
    tabStore.setNextReload(false);
    // 重新查询未读数据;
    await getUnReadMsgNum();
    onUserAfterLeave();
    await afterLoginSwitchProxyJump();
};
// 是否展示切换回主账号
const showMyEnterprise = () => {
    return (
        accountStore.accountTypeDesc &&
        accountStore.accountTypeDesc !== "主账号" &&
        ((enterpriseList.value.length && !mainBodyList.value.length) ||
            (!enterpriseList.value.length && mainBodyList.value.length) ||
            (enterpriseList.value.length && mainBodyList.value.length))
    );
};
/** -------------弹出框交互优化------------- */
// 移除一级弹出层他、隐藏动画前的钩子函数
const onUserAfterLeave = () => {
    enterpriseVisible.value = false;
    enterRoleVisible.value = false;
    mainBodyVisible.value = false;
};
// 鼠标移入切换企业的时候
const onMouseEnter = () => {
    setEnterpriseIndex();
};

// 鼠标移入面板提前请求企业列表
const { fn: showUserPopper, loading } = useLoading(async () => {
    await setEnterpriseList();
    currentMainBody.value = userStore.userInfo?.mainBody?.id;
    await setMainBodyList();
});
// 是否显示企业弹出框
const enterpriseVisible = ref(false);
// 控制企业后面的可用角色是否显示弹出框
const enterRoleVisible = ref(false);
const onClickChangeEnterprise = async () => {
    enterpriseVisible.value = true;
    mainBodyVisible.value = false;
};

// 是否显示企业弹出框
const mainBodyVisible = ref(false);
const onClickChangeMainBody = async () => {
    mainBodyVisible.value = true;
    enterpriseVisible.value = false;
};

onMounted(async () => {
    await setEnterpriseList();
});

const getUnReadMsgNum = async () => {
    const res = await userMessageService.getMsgNum();
    const allUnreadMsgNum = res.allType;
    userStore.setAllUnreadMsgNum(allUnreadMsgNum);
};
// 展示企业名称
const getInstitutionName = () => {
    if (accountStore.isAssociated) {
        return accountStore.mainBody?.enterpriseName;
    } else {
        return userStore.userInfo?.mainBody?.enterpriseName;
    }
};
// 获取主账号或者代理账号身份
const getCustomerRoles = (): number[] => {
    // 子账号返回代理账号的身份
    if (accountStore.isSubAccount) return userStore.userInfo?.proxyAccount?.roles || ([] as number[]);
    return userStore.userInfo?.customer?.roles || ([] as number[]);
};
// 主账号或代理账号是不是采购客户
const isPurchaseCustomer = () => {
    const roles = getCustomerRoles();
    return roles.includes(ROLE_TYPE.PURCHASE_CUSTOMER.code);
};

// 主账号或代理账号是不是仓储客户
const isWarehouseCustomer = () => {
    const roles = getCustomerRoles();
    return roles.includes(ROLE_TYPE.WAREHOUSE_CUSTOMER.code);
};
</script>

<style scoped lang="scss">
@use "./index.scss";
</style>
